import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import produce from 'utils/produce';

import { Alert } from 'components/base';

const QUALITY = {
  NONE: -1,
  POOR: 0,
  GOOD: 1,
  BEST: 2,
};

const trackPhotosQuality = qualityLabel => {
  console.log('TRACK', qualityLabel);

  try {
    ga('send', 'event', {
      eventCategory: 'editor',
      eventAction: 'photos_quality',
      eventLabel: qualityLabel,
    });
  } catch (err) {
    console.log('GA', err);
  }

  try {
    mixpanel.track('editor_photos_quality', {
      quality: qualityLabel,
    });
  } catch (err) {
    console.log('MIXPANEL', err);
  }
};
const PhotoQualityAlert = props => {
  const [quality, setQuality] = useState(QUALITY.NONE);

  useEffect(() => {
    let newQuality = QUALITY.NONE;

    if (props.visible && Object.keys(props.photosQuality).length > 0) {
      newQuality = QUALITY.BEST;

      const dpi = Math.min(...Object.entries(props.photosQuality).map(([, value]) => value));

      if (dpi <= 1) {
        newQuality = QUALITY.NONE;
      } else if (dpi < props.minDPI) {
        newQuality = QUALITY.POOR;
      } else if (dpi < props.bestDPI) {
        newQuality = QUALITY.GOOD;
      }
    }

    setQuality(newQuality);
  }, [props.bestDPI, props.minDPI, props.photosQuality, props.visible]);

  useEffect(() => {
    switch (quality) {
      case QUALITY.BEST:
        trackPhotosQuality('best');
        break;
      case QUALITY.GOOD:
        trackPhotosQuality('good');
        break;
      case QUALITY.POOR:
        trackPhotosQuality('poor');
        break;
      default:
        break;
    }
  }, [quality]);

  const className = classNames({
    'text-center': true,
    [props.className]: props.className,
  });

  if (quality === QUALITY.POOR) {
    return (
      <Alert type="danger" className={className}>
        This photo is too small and will not print clearly. Please upload a higher quality image.
        Original images (not an Instagram or screenshot) that are 5-10 MB and not zoomed-in will
        print best.
      </Alert>
    );
  }

  if (quality === QUALITY.GOOD) {
    return (
      <Alert type="warning" className={className}>
        This photo may print fuzzier than you’d like. Consider using a higher quality image.
        Original images (not an Instagram or screenshot) that are 5-10 MB and not zoomed-in will
        print best.
      </Alert>
    );
  }

  return null;
};

PhotoQualityAlert.propTypes = {
  photosQuality: PropTypes.object,
  minDPI: PropTypes.number,
  bestDPI: PropTypes.number,
  visible: PropTypes.bool,
  className: PropTypes.string,
};

PhotoQualityAlert.defaultProps = {
  photosQuality: {},
  visible: false,
  minDPI: 72,
  bestDPI: 300,
};

export const usePhotosQuality = (minDPI = 72) => {
  const [photosQuality, setPhotosQuality] = useState({});

  const handleOnPhotosQuality = (key, quality) => {
    setPhotosQuality(val =>
      produce(val, draft => {
        draft[key] = quality;
      }),
    );
  };

  let acceptableQuality = true;
  if (Object.keys(photosQuality).length > 0) {
    const qualityValue = Math.min(...Object.entries(photosQuality).map(([, value]) => value));
    acceptableQuality = qualityValue >= minDPI;
  }

  return [photosQuality, handleOnPhotosQuality, acceptableQuality];
};

export default PhotoQualityAlert;
